import { useState } from 'react';
import { formatarCentavos } from '@/pages/loja/utils';
import { horaBrasilia, parseDinheiroCentavos } from '../turno';
import type { CaixaTurno } from '../types';

interface CartaoAbrirCaixaProps {
  aoAbrir: (trocoCentavos: number) => Promise<{ ok: boolean; erro?: string }>;
  aoDispensar: () => void;
}

// Cartão "Abrir o caixa" — aparece acima dos produtos quando ainda não há turno.
// Não bloqueia nada; "Agora não" esconde até recarregar a página.
export function CartaoAbrirCaixa({ aoAbrir, aoDispensar }: CartaoAbrirCaixaProps) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function abrir() {
    const centavos = parseDinheiroCentavos(texto);
    if (centavos == null) {
      setErro('Digite o troco (ex.: 100 ou 100,00).');
      return;
    }
    setEnviando(true);
    setErro(null);
    const r = await aoAbrir(centavos);
    if (!r.ok) {
      setErro(r.erro || 'Não foi possível abrir o caixa agora.');
      setEnviando(false);
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center text-primary-600">
          <i className="ri-safe-2-line text-lg" />
        </span>
        <h2 className="text-base font-semibold text-foreground-950">Abrir o caixa</h2>
      </div>
      <p className="mt-1 text-sm text-secondary-500">Quanto de troco tem na gaveta?</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="text"
          inputMode="decimal"
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value.replace(/[^0-9.,]/g, ''));
            setErro(null);
          }}
          placeholder="0"
          className="w-full rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-lg font-bold tabular-nums text-foreground-950 outline-none focus:border-primary-500 sm:w-40"
        />
        <button
          type="button"
          onClick={() => void abrir()}
          disabled={enviando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          {enviando ? 'Abrindo…' : 'Abrir caixa'}
        </button>
        <button
          type="button"
          onClick={aoDispensar}
          className="cursor-pointer whitespace-nowrap text-sm font-medium text-secondary-500 underline-offset-2 hover:text-foreground-800 hover:underline"
        >
          Agora não
        </button>
      </div>
      {erro && <p className="mt-2 text-sm text-danger-600">{erro}</p>}
    </div>
  );
}

// Faixa fina do topo quando o caixa já está aberto.
export function FaixaCaixaAberto({ turno, aoSangria }: { turno: CaixaTurno; aoSangria?: () => void }) {
  return (
    <div className="shrink-0 border-b border-success-200 bg-success-50 px-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex flex-wrap items-center gap-x-1.5 text-sm font-medium text-success-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-lock-unlock-line" />
          </span>
          Caixa aberto às <span className="font-bold tabular-nums">{horaBrasilia(turno.aberto_em)}</span>{' '}
          · troco{' '}
          <span className="font-bold tabular-nums">
            {formatarCentavos(turno.troco_inicial_centavos)}
          </span>
          {turno.sangria_centavos > 0 && (
            <>
              {' '}
              · sangrias{' '}
              <span className="font-bold tabular-nums">− {formatarCentavos(turno.sangria_centavos)}</span>
            </>
          )}
        </p>
        {aoSangria && (
          <button
            type="button"
            onClick={aoSangria}
            className="flex min-h-[36px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-success-300 bg-white px-3 text-[13px] font-semibold text-success-800 transition-colors hover:bg-success-100"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-hand-coin-line" />
            </span>
            Sangria
          </button>
        )}
      </div>
    </div>
  );
}